import React, { useRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { useTranslation } from 'react-i18next';
import { newRequestKey, PaymentRequest, refundPaymentRequest } from 'api/paymentRequestsApi';
import { refundAllocations } from './paymentRequestForm';
import { euro, percent } from './format';
import { useFieldIds } from './fieldIds';

/**
 * Full or position-attributed partial refund (Q19). The API limits every
 * position and the payment to what is still refundable.
 */
const RefundModal: React.FC<{
  restaurantId: string;
  request: PaymentRequest;
  onClose: () => void;
  onDone: (request: PaymentRequest) => void;
}> = ({ restaurantId, request, onClose, onDone }) => {
  const { t } = useTranslation('reservations');
  const fieldId = useFieldIds('refund');
  const [refundKey] = useState(newRequestKey);
  const [full, setFull] = useState(true);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  // Two clicks can land before `busy` re-renders; the ref closes that gap.
  const submitting = useRef(false);
  const [error, setError] = useState('');
  // A request paid twice holds two Mollie payments; each is refunded on its own.
  const payments = request.payments ?? [];
  const [attemptId, setAttemptId] = useState(
    () =>
      payments.find((payment) => !payment.primary && payment.refundable.some((line) => line.remainingCents > 0))
        ?.attemptId ??
      payments[0]?.attemptId ??
      ''
  );
  const payment = payments.find((entry) => entry.attemptId === attemptId);
  const refundable = payment?.refundable ?? request.refundable;
  const remaining = refundable.reduce((sum, line) => sum + line.remainingCents, 0);
  const partial = refundAllocations(draft, refundable);
  const canSubmit = full ? remaining > 0 : partial.ok;

  const submit = async () => {
    if (!canSubmit || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      onDone(
        await refundPaymentRequest(
          restaurantId,
          request.id,
          {
            refundKey,
            ...(payment ? { attemptId: payment.attemptId } : {}),
            ...(full ? { full: true } : { allocations: partial.ok ? partial.allocations : [] }),
          }
        )
      );
    } catch (err: any) {
      setError(err.message);
      setBusy(false);
      submitting.current = false;
    }
  };

  return (
    <Modal open title={t('Refund payment')} maxWidth="sm" fullWidth onClose={busy ? undefined : onClose}>
      <Box style={{ display: 'grid', gap: 12 }}>
        {payments.length > 1 && (
          <TextField
            id={fieldId('payment')}
            select
            SelectProps={{ native: true }}
            size="small"
            variant="outlined"
            label={t('Payment to refund')}
            value={attemptId}
            disabled={busy}
            onChange={(event) => {
              setAttemptId(event.target.value);
              setDraft({});
            }}
          >
            {payments.map((entry) => (
              <option key={entry.attemptId} value={entry.attemptId}>
                {`${euro(entry.paidCents)} · ${entry.molliePaymentId ?? '–'} · ${
                  entry.primary ? t('Requested payment') : t('Additional payment')
                }`}
              </option>
            ))}
          </TextField>
        )}
        <Typography variant="text-3" block translation={null}>
          {t('Still refundable')}: {euro(remaining)}
        </Typography>
        <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant={full ? 'primary' : 'outlined'} translation={null} onClick={() => setFull(true)}>
            {t('Full refund')}
          </Button>
          <Button variant={!full ? 'primary' : 'outlined'} translation={null} onClick={() => setFull(false)}>
            {t('Partial refund by position')}
          </Button>
        </Box>
        {!full &&
          refundable
            .filter((line) => line.remainingCents > 0)
            .map((line) => {
              const source = request.items.lines[line.lineIndex];
              return (
                <TextField
            id={fieldId(`line-${line.lineIndex}`)}
                  key={line.lineIndex}
                  size="small"
                  variant="outlined"
                  label={`${source?.description ?? line.lineIndex + 1} (${percent(source?.vatRate ?? '0')} ${t('VAT')})`}
                  helperText={`${t('Remaining amount')}: ${euro(line.remainingCents)}`}
                  value={draft[line.lineIndex] ?? ''}
                  disabled={busy}
                  inputProps={{ inputMode: 'decimal' }}
                  onChange={(event) => {
                    const value = event.target.value;
                    setDraft((current) => ({ ...current, [line.lineIndex]: value }));
                  }}
                />
              );
            })}
        {!full && (
          <Typography variant="text-3" block translation={null}>
            {t('Refund amount')}: {euro(partial.ok ? partial.totalCents : 0)}
          </Typography>
        )}
        <Typography variant="text-3" color="subdued" block translation={null}>
          {t('The refund is sent to Mollie; the cancellation invoice is created once Mollie confirms it.')}
        </Typography>
        {error && (
          <Typography color="error" block translation={null}>
            <span role="alert">{error}</span>
          </Typography>
        )}
        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="outlined" translation={null} disabled={busy} onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" translation={null} loading={busy} disabled={!canSubmit || busy} onClick={submit}>
            {t('Refund')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RefundModal;
